import * as types from '../actionTypes';
import { OFFER_ASSET_CLIENT_UPDATED } from 'features/offer/edit/assets/client/eventTypes';
import { OFFER_PARTNER_UPDATED, OFFER_OCR_FILE_UPLOADED, COUNTIES_INVALIDATED } from '../eventTypes';
import initialState from 'reducers/initialState';

export default function reducer(state = initialState.clientData, action) {
  switch (action.type) {
    case types.LOAD_PARTNER_TYPES_SUCCEEDED:
      return {
        ...state,
        partnerTypes: Object.assign([], action.payload.partnerTypes, { isLoaded: true })
      };

    case types.LOAD_ID_TYPES_SUCCEEDED:
      return {
        ...state,
        idTypes: Object.assign([], action.payload.idTypes, { isLoaded: true })
      };

    case types.LOAD_PARTNER_ROLES_SUCCEEDED:
      return {
        ...state,
        partnerRoles: Object.assign([], action.payload.partnerRoles, { isLoaded: true })
      };

    case types.LOAD_COUNTRIES_SUCCEEDED:
      return {
        ...state,
        countries: Object.assign([], action.payload.countries, { isLoaded: true })
      };

    case types.LOAD_CITIZENSHIPS_SUCCEEDED:
      return {
        ...state,
        citizenships: Object.assign([], action.payload.citizenships, { isLoaded: true })
      };

    case types.LOAD_RELATIONS_SUCCEEDED:
      return {
        ...state,
        relations: Object.assign([], action.payload.relations, { isLoaded: true })
      };

    case types.LOAD_COUNTIES_STARTED:
      return {
        ...state,
        counties: {
          ...state.counties,
          byCountryId: {
            ...state.counties.byCountryId,
            [action.payload.countryId]: {
              ...state.counties.byCountryId[action.payload.countryId],
              loaded: false,
              loading: true,
              data: []
            }
          }
        }
      };

    case types.LOAD_COUNTIES_SUCCEEDED:
      return {
        ...state,
        counties: {
          ...state.counties,
          byCountryId: {
            ...state.counties.byCountryId,
            [action.payload.countryId]: {
              ...state.counties.byCountryId[action.payload.countryId],
              loaded: true,
              loading: false,
              data: action.payload.counties
            }
          }
        }
      };

    case COUNTIES_INVALIDATED:
      return {
        ...state,
        counties: {
          ...state.counties,
          byCountryId: {
            ...state.counties.byCountryId,
            [action.event.eventBody.countryId]: {
              ...state.counties.byCountryId[action.event.eventBody.countryId],
              loaded: false,
              loading: false
            }
          }
        }
      };

    case types.LOAD_CITIES_SUCCEEDED:
      return {
        ...state,
        cities: Object.assign([], action.payload.cities, { isLoaded: true })
      };

    case types.LOAD_DEFAULT_PARTNER_ADDRESS_STARTED:
      return {
        ...state,
        defaultAddress: {
          ...state.defaultAddress,
          [action.partnerTypeId]: {
            ...state.defaultAddress[action.partnerTypeId],
            isLoading: true,
            isLoaded: false
          }
        }
      };

    case types.LOAD_DEFAULT_PARTNER_ADDRESS_SUCCEEDED:
      return {
        ...state,
        defaultAddress: {
          ...state.defaultAddress,
          [action.partnerTypeId]: { ...action.payload, isLoading: false, isLoaded: true }
        }
      };

    case types.SET_BIRTH_DATE_ON_CLIENT: {
      return {
        ...state,
        clients: {
          ...state.clients,
          byUniqueId: {
            ...state.clients.byUniqueId,
            [action.uniqueId]: {
              ...state.clients.byUniqueId[action.uniqueId],
              generalData: {
                ...state.clients.byUniqueId[action.uniqueId].generalData,
                birthDate: action.birthDate
              }
            }
          }
        }
      };
    }

    case types.SET_DEFAULT_ADDRESS_ON_CLIENT: {
      let addresses = state.clients.byUniqueId[action.uniqueId]?.addresses?.map(address => Object.assign({}, address));
      let defaultAddress = [];

      if (addresses && Array.isArray(addresses)) addresses.unshift(action.defaultAddress);
      else defaultAddress.push(action.defaultAddress);

      return {
        ...state,
        clients: {
          ...state.clients,
          byUniqueId: {
            ...state.clients.byUniqueId,
            [action.uniqueId]: {
              ...state.clients.byUniqueId[action.uniqueId],
              addresses: addresses ?? defaultAddress
            }
          }
        }
      };
    }

    case types.LOAD_GDPR_SUCCEEDED:
      return {
        ...state,
        gdpr: Object.assign({}, action.payload.gdpr, { isLoaded: true })
      };

    case types.LOAD_CLIENT_FROM_STARBYTE_SUCCEEDED: {
      return {
        ...state,
        clients: {
          ...state.clients,
          byUniqueId: {
            ...state.clients.byUniqueId,
            [action.payload.uniqueId]: action.payload
          }
        }
      };
    }

    case types.LOAD_CLIENT_BY_ID_SUCCEEDED: {
      return {
        ...state,
        clients: {
          ...state.clients,
          byUniqueId: {
            ...state.clients.byUniqueId,
            [action.payload.uniqueId]: action.payload.client
          }
        }
      };
    }

    case OFFER_OCR_FILE_UPLOADED: {
      const { generalData, contactData, contactMode, addresses, partnerType } = action.event.eventBody;
      const { uniqueId } = action.event.eventBody.generalData;
      const client = {
        generalData,
        contactData,
        contactMode,
        addresses,
        partnerType: partnerType
      };

      return {
        ...state,
        clients: {
          ...state.clients,
          byUniqueId: {
            ...state.clients.byUniqueId,
            [uniqueId]: client
          }
        }
      };
    }

    case OFFER_ASSET_CLIENT_UPDATED:
    case OFFER_PARTNER_UPDATED: {
      const { uniqueId, fromPartnerQuery } = action.event.eventBody;
      const byUniqueId = { ...state.clients.byUniqueId };
      delete byUniqueId[uniqueId];

      return {
        ...state,
        clients: {
          ...state.clients,
          byUniqueId,
          fromPartnerQuery: {
            ...state.clients.fromPartnerQuery,
            status: fromPartnerQuery,
            [uniqueId]: {
              isExternalVerificationInProgress: false
            }
          }
        }
      };
    }

    case types.FINISH_PARTNER_QUERY: {
      return {
        ...state,
        clients: {
          ...state.clients,
          fromPartnerQuery: {
            ...state.clients.fromPartnerQuery,
            status: false
          }
        }
      };
    }

    case types.LOAD_PARTNER_FROM_QUERY_PROGRESS_FAILED:
    case types.LOAD_PARTNER_FROM_QUERY_PROGRESS_STARTED: {
      const fromPartnerQuery = action.payload.uniqueId;
      const elem = state.clients.fromPartnerQuery[fromPartnerQuery] || {};
      return {
        ...state,
        clients: {
          ...state.clients,
          fromPartnerQuery: {
            ...state.clients.fromPartnerQuery,
            [fromPartnerQuery]: {
              ...elem,
              isExternalVerificationInProgress: action.payload.isExternalVerificationInProgress
            }
          }
        }
      };
    }

    case types.OFFER_COMPANY_INFO_NOT_FOUND: {
      const byUniqueId = action.event.eventBody.uniqueId;
      const elem = state.clients.byUniqueId[byUniqueId] || {};
      return {
        ...state,
        clients: {
          ...state.clients,
          byUniqueId: {
            ...state.clients.byUniqueId,
            [byUniqueId]: {
              ...elem,
              generalData: {
                ...elem.generalData,
                uniqueId: action.event.eventBody.uniqueId
              }
            }
          }
        }
      };
    }

    case types.GET_COMPANY_INFO_SUCCEEDED: {
      const byUniqueId = action.payload.uniqueId;
      const elem = state.clients.byUniqueId[byUniqueId] || {};
      return {
        ...state,
        clients: {
          ...state.clients,
          byUniqueId: {
            ...state.clients.byUniqueId,
            [byUniqueId]: {
              ...elem,
              isExternalVerificationInProgress: action.payload.isExternalVerificationInProgress,
              generalData: {
                ...elem.generalData,
                masterPartnerId: action.payload.partnerId,
                uniqueId: action.payload.masterUniqueId ?? action.payload.uniqueId,
                partnerName: action.payload.partnerName,
                comercialRegistration: action.payload.comercialRegistration,
                legalFormId: action.payload.legalFormId,
                registrationDate: action.payload.registrationDate,
                dealerId: action.payload.dealerId,
                partnerGroupId: action.payload.partnerGroupId
              },
              contactData: action.payload.contactData,
              contactMode: action.payload.contactMode,
              addresses: action.payload.addressData,
              dealerContactInfos: action.payload.dealerContactInfos
            }
          }
        }
      };
    }

    case types.LOAD_PARTNER_FROM_QUERY: {
      const uniqueId = action.payload.uniqueId;
      const elem = state.clients.fromPartnerQuery[uniqueId] || {};
      return {
        ...state,
        clients: {
          ...state.clients,
          fromPartnerQuery: {
            ...state.clients.fromPartnerQuery,
            [uniqueId]: {
              ...elem,
              generalData: {
                ...elem.generalData,
                masterPartnerId: action.payload.partnerId,
                uniqueId: action.payload.uniqueId,
                partnerName: action.payload.partnerName,
                comercialRegistration: action.payload.comercialRegistration,
                legalFormId: action.payload.legalFormId,
                registrationDate: action.payload.registrationDate,
                dealerId: action.payload.dealerId,
                partnerGroupId: action.payload.partnerGroupId
              },
              contactData: action.payload.contactData,
              contactMode: action.payload.contactMode,
              addresses: action.payload.addressData
            }
          }
        }
      };
    }

    case types.LOAD_PARTNER_HIERARCHY_SUCCEEDED: {
      const partnerGroupId = action.payload.partnerGroupId;
      return {
        ...state,
        partnerHierarchies: {
          ...state.partnerHierarchies,
          [partnerGroupId]: Object.assign([], action.payload.partnerHierarchy, { isLoaded: true })
        }
      };
    }

    case types.LOAD_OCR_FILE_EXTENSIONS_SUCCEDED:
      return {
        ...state,
        defaultOCRFileExtensions: Object.assign([], action.payload.defaultOCRFileExtensions.fileExtensions, {
          isLoaded: true
        })
      };

    default:
      return state;
  }
}
